import React, { useState, useEffect } from 'react';
import { ArrowLeft, CheckCircle, Calendar, CreditCard, Award } from 'lucide-react';
import logo from '../../assets/img/56.png';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

const CursosExtensionPublic = ({ setCurrentPage }) => {
    const [cursos, setCursos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inscripcionHabilitada] = useState(true); // Siempre habilitada globalmente, se valida por curso individuamente

    useEffect(() => {
        loadCursos();
    }, []);

    const loadCursos = async () => {
        try {
            // Cargar cursos disponibles públicamente (sin autenticación)
            const response = await fetch(`${API_URL}/auth/cursos-extension/disponibles`);
            const data = await response.json();
            setCursos(data.cursos || []);
        } catch (error) {
            console.error('Error loading cursos:', error);
            setCursos([]);
        } finally {
            setLoading(false);
        }
    };

    const isDeadlinePassed = (curso) => {
        if (!curso) return false;
        const deadline = curso.fecha_limite_inscripcion || curso.fecha_inicio;
        if (!deadline) return false;
        const deadlineDate = new Date(deadline);
        deadlineDate.setHours(23, 59, 59, 999);
        return new Date() > deadlineDate;
    };

    const handleInscribirse = async (curso) => {
        // En lugar de periodo global, validamos solo cupos y plazos del curso especifico
        if (curso.disponibles <= 0) {
            alert('Este curso ya no tiene cupos disponibles.');
            return;
        }

        if (isDeadlinePassed(curso)) {
            alert('El plazo de inscripción para este curso ha vencido.');
            return;
        }

        // Navegar al registro de cursos de extensión pasando el curso completo seleccionado
        setCurrentPage('registro-cursos-extension', {
            cursoId: curso.id_curso,
            cursoNombre: curso.nombre,
            cursoCuotas: curso.numero_cuotas || 1,
            cursoCosto: curso.costo_curso || curso.costo || 0
        });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        // Extract YYYY-MM-DD from any format (date-only or ISO timestamp)
        // This avoids timezone shift from new Date() parsing UTC strings
        const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            const date = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
            return date.toLocaleDateString('es-BO', { day: '2-digit', month: 'short', year: 'numeric' });
        }
        const date = new Date(dateStr);
        return date.toLocaleDateString('es-BO', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return '-';
        return timeStr.substring(0, 5); // HH:MM
    };

    // Parse modulos JSON if it's a string
    const parseModulos = (modulos) => {
        if (!modulos) return [];
        if (typeof modulos === 'string') {
            try {
                return JSON.parse(modulos);
            } catch {
                return [];
            }
        }
        return Array.isArray(modulos) ? modulos : [];
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header azul oscuro */}
            <div className="bg-[#1e3a8a] h-3"></div>

            <div className="container mx-auto px-4 py-8">
                <div className="max-w-6xl mx-auto">
                    {/* Header con logo y botón volver */}
                    <div className="flex items-center justify-between mb-8">
                        <button
                            onClick={() => setCurrentPage('landing')}
                            className="flex items-center gap-2 text-[#1e3a8a] hover:underline font-medium"
                        >
                            <ArrowLeft size={20} />
                            Volver
                        </button>

                        <div className="flex items-center gap-3">
                            <img src={logo} alt="EBID Logo" className="w-12 h-12 object-contain" />
                            <div className="text-right hidden md:block">
                                <h1 className="text-lg font-bold text-[#2c3e7d]">Sistema Académico</h1>
                                <p className="text-[#2c3e7d] text-xs">Escuela Boliviana Intercultural de Danza</p>
                            </div>
                        </div>
                    </div>

                    {/* Título */}
                    <div className="text-center mb-10">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                            Cursos de Extensión
                        </h2>
                        <p className="text-gray-600">
                            Inscríbete en nuestros cursos y talleres disponibles
                        </p>
                        <p className="text-sm text-gray-600 mt-3">
                            ¿Ya tienes una cuenta?{' '}
                            <button
                                onClick={() => setCurrentPage('login')}
                                className="text-[#1e3a8a] font-semibold hover:underline"
                            >
                                Inicia sesión aquí
                            </button>
                        </p>
                    </div>

                    {/* Alerta período cerrado */}
                    {!inscripcionHabilitada && (
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-yellow-700">
                                        <strong>Inscripciones cerradas.</strong> El periodo de inscripción a cursos de extensión no está habilitado actualmente.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    {loading ? (
                        <div className="text-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a8a] mx-auto"></div>
                            <p className="text-gray-500 mt-4">Cargando cursos...</p>
                        </div>
                    ) : cursos.length === 0 ? (
                        <div className="text-center py-12 bg-white rounded-xl shadow">
                            <p className="text-gray-500 text-lg">No hay cursos disponibles en este momento</p>
                            <p className="text-gray-400 text-sm mt-2">Vuelve pronto para ver nuevas ofertas</p>
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {cursos.map((curso) => {
                                const modulos = parseModulos(curso.modulos);

                                return (
                                    <div
                                        key={curso.id_curso}
                                        className="bg-white rounded-2xl shadow-lg overflow-hidden"
                                    >
                                        {/* Header con gradiente */}
                                        <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-400 h-16 flex items-center justify-center">
                                            <Award size={32} className="text-white/80" />
                                        </div>

                                        {/* Contenido del card */}
                                        <div className="p-6">
                                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                                                <div>
                                                    <h3 className="text-2xl font-bold text-gray-900 mb-1">
                                                        {curso.nombre}
                                                    </h3>
                                                    <p className="text-gray-600 text-sm">
                                                        {curso.descripcion || 'Aprende con nuestros expertos en este curso especializado.'}
                                                    </p>
                                                </div>

                                                <div className="flex flex-col items-end gap-2">
                                                    <div className="text-right">
                                                        <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Disponibilidad</p>
                                                        <p className={`text-lg font-bold ${curso.disponibles <= 0 ? 'text-red-500' : 'text-green-600'}`}>
                                                            {curso.disponibles <= 0 ? 'SIN CUPOS' : `${curso.disponibles} espacios`}
                                                        </p>
                                                        <p className="text-[10px] text-gray-400">de {curso.capacidad} totales</p>
                                                    </div>
                                                    <button
                                                        onClick={() => handleInscribirse(curso)}
                                                        disabled={!inscripcionHabilitada || curso.disponibles <= 0 || isDeadlinePassed(curso)}
                                                        className={`shrink-0 px-6 py-2.5 rounded-lg font-semibold transition ${inscripcionHabilitada && curso.disponibles > 0 && !isDeadlinePassed(curso)
                                                            ? 'bg-[#1e3a8a] text-white hover:bg-[#2c4a9a]'
                                                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                            }`}
                                                    >
                                                        {curso.disponibles <= 0 ? 'Cupos Llenos' : isDeadlinePassed(curso) ? 'Plazo Vencido' : 'Inscribirse'}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Info del curso */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                                                <div className="flex items-center gap-2 text-sm text-gray-700">
                                                    <Calendar size={18} className="text-blue-500" />
                                                    <span><strong>Inicio:</strong> {formatDate(curso.fecha_inicio)}</span>
                                                </div>
                                            </div>

                                            {/* Tabla de Módulos */}
                                            {modulos.length > 0 && (
                                                <div>
                                                    <h4 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">
                                                        Módulos del Curso
                                                    </h4>
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full text-sm">
                                                            <thead>
                                                                <tr className="text-left text-gray-600 border-b">
                                                                    <th className="py-2 px-2 font-medium">Modulo</th>
                                                                    <th className="py-2 px-2 font-medium">Duración</th>
                                                                    <th className="py-2 px-2 font-medium">Carga Horaria</th>
                                                                    <th className="py-2 px-2 font-medium">Sesiones</th>
                                                                    <th className="py-2 px-2 font-medium">Fecha</th>
                                                                    <th className="py-2 px-2 font-medium">Días</th>
                                                                    <th className="py-2 px-2 font-medium">Hora</th>
                                                                    <th className="py-2 px-2 font-medium">Docente</th>
                                                                    <th className="py-2 px-2 font-medium">Sala</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {modulos.map((mod, idx) => (
                                                                    <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                                                                        <td className="py-2 px-2 font-medium text-gray-900">{mod.nombre || '-'}</td>
                                                                        <td className="py-2 px-2 text-gray-600">{mod.duracion ? `${mod.duracion} hrs` : '-'}</td>
                                                                        <td className="py-2 px-2 text-gray-600">{mod.carga_horaria ? `${mod.carga_horaria} hrs` : '-'}</td>
                                                                        <td className="py-2 px-2 text-gray-600">{mod.sesiones || '-'}</td>
                                                                        <td className="py-2 px-2 text-gray-600">
                                                                            {mod.fecha_inicio && mod.fecha_fin
                                                                                ? `${formatDate(mod.fecha_inicio)} - ${formatDate(mod.fecha_fin)}`
                                                                                : formatDate(mod.fecha_inicio) || '-'}
                                                                        </td>
                                                                        <td className="py-2 px-2 text-gray-600 capitalize">{mod.dias?.replace(/,/g, ', ') || '-'}</td>
                                                                        <td className="py-2 px-2 text-gray-600">
                                                                            {mod.hora_inicio && mod.hora_fin
                                                                                ? `${formatTime(mod.hora_inicio)} - ${formatTime(mod.hora_fin)}`
                                                                                : '-'}
                                                                        </td>
                                                                        <td className="py-2 px-2 text-gray-600">{mod.facilitador || '-'}</td>
                                                                        <td className="py-2 px-2 text-gray-600">{mod.sala || '-'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Sin módulos */}
                                            {modulos.length === 0 && (
                                                <div className="text-center py-4 text-gray-400 text-sm italic">
                                                    Información de módulos pendiente de publicación
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}


                </div>
            </div>
        </div>
    );
};

export default CursosExtensionPublic;

